import { SettingsV1SystemManagementServiceGetSystemManagementConfig, SettingsV1SystemManagementServiceGetSystemManagementQuotaOverrides } from 'c1gen/permissions'
import { QuotaRef, DefaultQuotas } from 'c1gen/quotas'
import { usePermissionChecker } from 'components/no-ui/capability'
import { GetSystemManagementConfigRequest, GetSystemManagementQuotaOverridesRequest, SystemManagementConfig, SystemManagementQuotaOverrides } from 'pbts/c1/api/settings/v1/system_management'
import { createContext, useContext, ReactNode, FC, useMemo } from 'react'
import { getSystemManagementConfig, getSystemManagementQuotaOverrides } from 'redux_toolkit/system-management'

interface TenantConfigContextValue {
    config: SystemManagementConfig | undefined
    quotaOverrides: SystemManagementQuotaOverrides | undefined
    isConfigLoading: boolean
    isQuotaOverridesLoading: boolean
    configError: unknown
    quotaOverridesError: unknown
}

const TenantConfigContext = createContext<TenantConfigContextValue>({
    config: undefined,
    quotaOverrides: undefined,
    isConfigLoading: false,
    isQuotaOverridesLoading: false,
    configError: undefined,
    quotaOverridesError: undefined,
})

const useTenantConfig = (): TenantConfigContextValue => {
    const context = useContext(TenantConfigContext)
    if (!context) {
        throw new Error('useTenantConfig must be used within a TenantConfigContextProvider')
    }
    return context
}

export const useQuota = (quotaRef: QuotaRef): number => {
    const { quotaOverrides } = useTenantConfig()

    // Check if the specific quota exists in overrides
    const overrideValue = quotaOverrides?.quotaOverrides[quotaRef]
    if (overrideValue !== undefined && overrideValue !== null) {
        return Number(overrideValue)
    }

    // Fall back to default if not found in overrides
    return DefaultQuotas[quotaRef]
}

export const useSystemManagementConfig = (): SystemManagementConfig | undefined => {
    const { config } = useTenantConfig()
    return config
}

interface TenantConfigContextProviderProps {
    children: ReactNode
}

export const TenantConfigContextProvider: FC<TenantConfigContextProviderProps> = ({ children }) => {
    const hasPermission = usePermissionChecker()

    const hasSystemManagementConfig = hasPermission(SettingsV1SystemManagementServiceGetSystemManagementConfig)
    const hasSystemManagementQuotaOverrides = hasPermission(SettingsV1SystemManagementServiceGetSystemManagementQuotaOverrides)

    const {
        data: config,
        isLoading: isConfigLoading,
        error: configError,
    } = getSystemManagementConfig.useGetSystemManagementConfig(
        GetSystemManagementConfigRequest.create({}),
        { actionable: hasSystemManagementConfig },
    )

    const {
        data: quotaOverrides,
        isLoading: isQuotaOverridesLoading,
        error: quotaOverridesError,
    } = getSystemManagementQuotaOverrides.useGetSystemManagementQuotaOverrides(
        GetSystemManagementQuotaOverridesRequest.create({}),
        { actionable: hasSystemManagementQuotaOverrides },
    )

    const contextValue = useMemo(() => ({
        config,
        quotaOverrides,
        isConfigLoading,
        isQuotaOverridesLoading,
        configError,
        quotaOverridesError,
    }), [config, quotaOverrides, isConfigLoading, isQuotaOverridesLoading, configError, quotaOverridesError])

    return (
        <TenantConfigContext.Provider value={contextValue}>
            {children}
        </TenantConfigContext.Provider>
    )
}

export default TenantConfigContextProvider
